import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Avatar } from '@mui/material';
import moment from 'moment';

import WebsiteAvatar from 'src/components/WebsiteAvatar';
import { formatStatusDisplay } from 'src/utils/statusUtils';
import { getStatusColor } from 'src/constants/statusColors';
import ActionColumn from 'src/pages/Reports/components/ActionColumn';
import DateEditModal from 'src/pages/Reports/components/DateEditModal';
import { hasCustomReportDate } from 'src/utils/queryNotesUtils';
import { getQueryDateForDisplay } from 'src/utils/queryUtils';

const EffortRow = ({
  effort,
  isExternal = false,
  onRemoveExternalEffort,
  onEditExternalEffort,
  company,
  showActions = true,
  statusType = 'inProgress',
  onDateUpdate,
  dateRanges,
  dateFilter,
  allReports,
  allReportsMutate,
  allowDateEdit = true,
}) => {
  const [dateModalOpen, setDateModalOpen] = useState(false);
  // Normalize data structure for unified handling
  const normalizedEffort = {
    id: effort.id,
    title: effort.title,
    outlet: isExternal ? effort.outlet : effort.mediaOutlet,
    image: isExternal ? null : effort.qwotedImage,
    date: isExternal ? effort.date : effort.updateTime,
    status: effort.status,
    link: isExternal ? effort.link : effort.mediaURL,
    profileLink: isExternal ? null : `/query-profile/${effort.id}`,
  };

  // Determine which date to display
  // For regular queries: use the same priority order as filterByDateRange
  // For external efforts: check reportDate field directly, fall back to date
  const displayDate = isExternal ? effort.reportDate || normalizedEffort.date : getQueryDateForDisplay(effort);

  const isUsingReportDate = isExternal ? Boolean(effort.reportDate) : hasCustomReportDate(effort);

  const handleDateClick = () => {
    setDateModalOpen(true);
  };

  const handleDateUpdate = (queryId, newReportDate) => {
    if (onDateUpdate) {
      onDateUpdate(queryId, newReportDate);
    }
  };

  const getStatusText = () => {
    return `${formatStatusDisplay(normalizedEffort.status)}${isExternal ? ' (Additional)' : ''}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        p: 1,
        '&:hover': { bgcolor: 'grey.50' },
        borderBottom: '1px solid #eee',
      }}
    >
      {/* Media Outlet Column */}
      <Box sx={{ flex: 1.5, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
        {isExternal ? (
          <WebsiteAvatar url={effort.outletLink || normalizedEffort.link} outlet={normalizedEffort.outlet} size={40} />
        ) : (
          <Avatar
            src={normalizedEffort.image}
            sx={{
              width: 40,
              height: 40,
              fontSize: '1rem',
              fontWeight: 'bold',
              backgroundColor: 'white',
              '& img': {
                objectFit: 'contain',
              },
            }}
          />
        )}
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.2,
            minWidth: 0,
          }}
        >
          {normalizedEffort.outlet}
        </Typography>
      </Box>

      {/* Query Title Column */}
      <Typography
        variant="body2"
        sx={{
          flex: 4,
          minWidth: 0,
          fontWeight: '600',
          display: '-webkit-box',
          alignContent: 'center',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1.2,
          pr: 2,
        }}
      >
        {normalizedEffort.title}
      </Typography>

      {/* Date Column */}
      <Typography
        variant="body2"
        onClick={allowDateEdit ? handleDateClick : undefined}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          pr: 2,
          cursor: allowDateEdit ? 'pointer' : 'default',
          color: isUsingReportDate ? 'primary.main' : 'text.primary',
          fontWeight: isUsingReportDate ? 600 : 400,
          ...(allowDateEdit && {
            '&:hover': {
              backgroundColor: 'grey.300',
              borderRadius: 1,
            },
          }),
        }}
        title={allowDateEdit ? (isUsingReportDate ? 'Custom report date (click to edit)' : 'Click to set custom report date') : undefined}
      >
        {moment(displayDate).format('MMM DD, YYYY')}
      </Typography>

      {/* Status Column */}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          color: `${getStatusColor(normalizedEffort.status)?.paletteKey || 'primary'}.main`,
          fontWeight: '700',
          borderRadius: 1,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          alignContent: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: 1.2,
          pr: 2,
        }}
      >
        {getStatusText()}
      </Typography>

      <ActionColumn
        effort={effort}
        normalizedEffort={normalizedEffort}
        isExternal={isExternal}
        onRemoveExternalEffort={onRemoveExternalEffort}
        onEditExternalEffort={onEditExternalEffort}
        company={company}
        showActions={showActions}
      />

      <DateEditModal
        open={dateModalOpen}
        onClose={() => setDateModalOpen(false)}
        effort={effort}
        isExternal={isExternal}
        onSuccess={handleDateUpdate}
        company={company}
        dateRanges={dateRanges}
        dateFilter={dateFilter}
        allReports={allReports}
        allReportsMutate={allReportsMutate}
      />
    </Box>
  );
};

EffortRow.propTypes = {
  effort: PropTypes.object.isRequired,
  isExternal: PropTypes.bool,
  onRemoveExternalEffort: PropTypes.func,
  onEditExternalEffort: PropTypes.func,
  company: PropTypes.string,
  showActions: PropTypes.bool,
  statusType: PropTypes.oneOf(['wins', 'inProgress']).isRequired,
  onDateUpdate: PropTypes.func,
  dateRanges: PropTypes.object,
  dateFilter: PropTypes.string,
  allReports: PropTypes.array,
  allReportsMutate: PropTypes.func,
  allowDateEdit: PropTypes.bool,
};

export default EffortRow;
